import { CalendarDaysIcon, ClockIcon, SunIcon } from '@heroicons/react/24/outline';
import { ErrorMessage } from '@hookform/error-message';
import CT from '@marketplace-ai/tags';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FieldError, FormLayout, TextInput } from '~/components';
import {
  useNegotiationBaseline,
  useNegotiationInputsDisabled,
  useNegotiationResponseType,
} from '../provider';
import { TermCompareSection } from '../sub-components/TermCompareSection';
import { getAllocatedShifts, getExpectedShifts } from '../utils';

const TAGS = CT.employer.negotiation;

export const ShiftDistribution = () => {
  const negotiation = useNegotiationBaseline();
  const inputsDisabled = useNegotiationInputsDisabled();
  const responseType = useNegotiationResponseType();
  const {
    formState: { errors },
  } = useFormContext();

  const [weekdayShiftsChanged, setWeekdayShiftsChanged] = useState({
    numDayShifts: false,
    numEveShifts: false,
    numNightShifts: false,
  });
  const [weekendShiftsChanged, setWeekendShiftsChanged] = useState({
    numOfWeDayShifts: false,
    numOfWeEveShifts: false,
    numOfWeNightShifts: false,
  });
  const [holidayShiftsChanged, setHolidayShiftsChanged] = useState({
    numHolDayShifts: false,
    numHolEveShifts: false,
    numHolNightShifts: false,
  });

  const [
    numDayShifts,
    numEveShifts,
    numNightShifts,
    numOfWeDayShifts,
    numOfWeEveShifts,
    numOfWeNightShifts,
    numHolDayShifts,
    numHolEveShifts,
    numHolNightShifts,
    numOfFtes,
    fteShiftsPerYear,
    numOfShifts,
  ] = useWatch({
    name: [
      'numDayShifts',
      'numEveShifts',
      'numNightShifts',
      'numOfWeDayShifts',
      'numOfWeEveShifts',
      'numOfWeNightShifts',
      'numHolDayShifts',
      'numHolEveShifts',
      'numHolNightShifts',
      'numOfFtes',
      'fteShiftsPerYear',
      'numOfShifts',
    ],
  });

  // [MCV-1645] Single source of truth for the running total, shared with the
  // zod resolver (`buildNegotiationSchema` -> `getShiftAllocationError`) via
  // the same `getAllocatedShifts`/`getExpectedShifts` helpers. No effect here
  // sets/clears the error itself anymore -- `root.shiftAllocation` comes
  // entirely from the resolver on submit/trigger, so there is one rule
  // instead of two that could disagree.
  const watchedValues = {
    numDayShifts,
    numEveShifts,
    numNightShifts,
    numOfWeDayShifts,
    numOfWeEveShifts,
    numOfWeNightShifts,
    numHolDayShifts,
    numHolEveShifts,
    numHolNightShifts,
    numOfFtes,
    fteShiftsPerYear,
  };
  const allocatedShifts = useMemo(() => getAllocatedShifts(watchedValues), [watchedValues]);
  const expectedShifts = useMemo(
    () =>
      responseType === 'job'
        ? Number.isFinite(Number(numOfShifts))
          ? Number(numOfShifts)
          : 0
        : getExpectedShifts(watchedValues),
    [numOfShifts, responseType, watchedValues]
  );

  useEffect(() => {
    setWeekdayShiftsChanged({
      numDayShifts: numDayShifts !== negotiation?.numDayShifts,
      numEveShifts: numEveShifts !== negotiation?.numEveShifts,
      numNightShifts: numNightShifts !== negotiation?.numNightShifts,
    });
    setWeekendShiftsChanged({
      numOfWeDayShifts: numOfWeDayShifts !== negotiation?.numOfWeDayShifts,
      numOfWeEveShifts: numOfWeEveShifts !== negotiation?.numOfWeEveShifts,
      numOfWeNightShifts: numOfWeNightShifts !== negotiation?.numOfWeNightShifts,
    });
    setHolidayShiftsChanged({
      numHolDayShifts: numHolDayShifts !== negotiation?.numHolDayShifts,
      numHolEveShifts: numHolEveShifts !== negotiation?.numHolEveShifts,
      numHolNightShifts: numHolNightShifts !== negotiation?.numHolNightShifts,
    });
  }, [
    numDayShifts,
    numEveShifts,
    numNightShifts,
    numOfWeDayShifts,
    numOfWeEveShifts,
    numOfWeNightShifts,
    numHolDayShifts,
    numHolEveShifts,
    numHolNightShifts,
    negotiation,
  ]);

  return (
    <FormLayout className="mt-4">
      <div className="flex flex-wrap items-center gap-2 bg-white p-4 rounded-lg">
        <p>Available Shifts</p>
        <p>
          {allocatedShifts} / {expectedShifts}
        </p>
        <ErrorMessage
          errors={errors}
          name="root.shiftAllocation"
          render={({ message }) => (
            // FieldError's `className` REPLACES its default `mt-1.5` rather than
            // appending to it, so `ml-2` alone sets this message off from the
            // "X / Y" total it follows.
            <FieldError
              message={message}
              className="ml-2"
              tabIndex={-1}
              cyTag="negotiation-shift-allocation-error"
            />
          )}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TermCompareSection
          icon={<CalendarDaysIcon className="h-5 w-5" />}
          changedTermsCount={Object.values(weekdayShiftsChanged).filter(Boolean).length}
          title="Weekday Shifts"
          terms={[
            {
              label: 'Day',
              value: negotiation?.numDayShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numDayShifts"
                  flagged={weekdayShiftsChanged.numDayShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekDayInput}
                />
              ),
            },
            {
              label: 'Evening',
              value: negotiation?.numEveShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numEveShifts"
                  flagged={weekdayShiftsChanged.numEveShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekEveningInput}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.numNightShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numNightShifts"
                  flagged={weekdayShiftsChanged.numNightShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekNightInput}
                />
              ),
            },
          ]}
        />
        <TermCompareSection
          icon={<SunIcon className="h-5 w-5" />}
          changedTermsCount={Object.values(weekendShiftsChanged).filter(Boolean).length}
          title="Weekend Shifts"
          terms={[
            {
              label: 'Day',
              value: negotiation?.numOfWeDayShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numOfWeDayShifts"
                  flagged={weekendShiftsChanged.numOfWeDayShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekendDayInput}
                />
              ),
            },
            {
              label: 'Evening',
              value: negotiation?.numOfWeEveShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numOfWeEveShifts"
                  flagged={weekendShiftsChanged.numOfWeEveShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekendEveningInput}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.numOfWeNightShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numOfWeNightShifts"
                  flagged={weekendShiftsChanged.numOfWeNightShifts}
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.weekendNightInput}
                />
              ),
            },
          ]}
        />
        <TermCompareSection
          icon={<ClockIcon className="h-5 w-5" />}
          changedTermsCount={Object.values(holidayShiftsChanged).filter(Boolean).length}
          title="Holiday Shifts"
          terms={[
            {
              label: 'Day',
              value: negotiation?.numHolDayShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numHolDayShifts"
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.holidayDayInput}
                />
              ),
            },
            {
              label: 'Evening',
              value: negotiation?.numHolEveShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numHolEveShifts"
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.holidayEveningInput}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.numHolNightShifts,
              newValueField: (
                <TextInput
                  type="number"
                  name="numHolNightShifts"
                  disabled={inputsDisabled}
                  cyTag={TAGS.shiftDistributionForm.holidayNightInput}
                />
              ),
            },
          ]}
        />
      </div>
    </FormLayout>
  );
};
